import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import {
    ErrorId,
    FieldId,
    FieldContent,
    FieldValidationErrors,
    ViewModel,
    MilitaryStatus,
    Organization,
    Metadata,
    FirstResponderStatus,
    MedicalProfessionalStatus,
    ExtendedFieldValidationErrors,
    ExtendedFieldId,
    Validator,
    ExtendedErrorId,
    FormValidationOptions,
    WithCoreFields,
    InactiveMilitaryPersonalInfoViewModel,
    Country,
} from '../types/types';
import { assertValidFieldId } from '../types/assertions';
import { getFieldValidationErrorsEmpty, getExtendedFieldValidationErrorsEmpty } from '../types/empties';
import { ACCEPTED_DOC_MIME_TYPES, SSN_STRING_LENGTH } from '../../constants';
import { logger } from '../utils/logger/logger';
import { FieldIdEnum, StateEnum } from '../types/runtimeTypes';
import { getOverridenValidator } from './validatorOverride';
import { getCustomValidatorFields, getCustomValidator } from './customValidators';
import { getSafe } from '../utils/objects';

const DECIMAL_RADIX = 10;
const ADOLESCENT_AGE = 16;
const MIN_BIRTHYEAR = 1900;
const MAX_BIRTHYEAR = 3000;

export const VALID_PHONE_NUMBER_REGEXPS = [
    /^\+?0*\d{10,}(x[0-9]{0,5})?$/,
    /(^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$)/,
];


export const validateMetadata = (metadata: Metadata): boolean => {
    const metadataKeyCount = Object.keys(metadata).length;

    // TOOD: Other metadata restrictions?
    if (metadataKeyCount > 20) {
        logger.error('Invalid number of metadata keys (max 20)', 'metadata keys');
        return false;
    }
    return true;
};

export const ensureMaxMetadataKeyValueLengths = (metadata: Metadata): Metadata => {
    const modifiedMetadata: Metadata = {};
    // TODO fix next line
    // eslint-disable-next-line
    for (const key in metadata) {
        let newKey = key;
        let newValue = metadata[key] as string;

        if (newValue.length > 256) {
            logger.warn(`Truncating metadata value '${metadata[key]}' for key '${newKey}' (max length 256 characters).`);
            newValue = newValue.substring(0, 256);
        }
        if (newKey.length > 256) {
            logger.warn(`Truncating metadata key '${key}' (max length 256 characters).`);
            newKey = newKey.substring(0, 256);
        }

        modifiedMetadata[newKey] = newValue;
    }
    return modifiedMetadata;
};

export const getFieldValidationErrors = (viewModel: ViewModel, formValidationOptions?: FormValidationOptions): FieldValidationErrors => {
    const fieldValidationErrors = getEmptyFieldValidationErrors();
    const metadata = getSafe(() => (viewModel as WithCoreFields).metadata, null);

    // TODO fix next line
    // eslint-disable-next-line
    for (const fieldId in fieldValidationErrors) {
        if (viewModel.hasOwnProperty(fieldId)) {
            const fieldsToSkip = getSafe(() => viewModel.fieldsToSkipValidation);
            if (Array.isArray(fieldsToSkip) && fieldsToSkip.indexOf(fieldId as FieldId) > -1) {
                continue;
            }
            const fieldValue = viewModel[fieldId] ? viewModel[fieldId] : '';
            fieldValidationErrors[fieldId] = validateFieldById(fieldId as FieldId, fieldValue, formValidationOptions);
        } else if (metadata !== null && typeof metadata === 'object' && metadata.hasOwnProperty(fieldId)) {
            const fieldValue = metadata[fieldId] ? metadata[fieldId] : '';
            fieldValidationErrors[fieldId] = validateFieldById(fieldId as FieldId, fieldValue, formValidationOptions);
        }
    }
    return fieldValidationErrors;
};

export const isFormFilled = (viewModel: ViewModel, formValidationOptions?: FormValidationOptions): boolean => {
    const customValidatorFields = getCustomValidatorFields();

    // TODO fix next line
    // eslint-disable-next-line
    for (const fieldId in viewModel) {
        const fieldValue = viewModel[fieldId] ? viewModel[fieldId] : '';
        const isFieldSkipped = viewModel.fieldsToSkipValidation && viewModel.fieldsToSkipValidation.includes(fieldId as FieldId);

        if (FieldIdEnum[fieldId] && typeof validateFieldById(fieldId, fieldValue, formValidationOptions) === 'string' && !isFieldSkipped) {
            return false;
        }
    }

    const metadata = getSafe(() => (viewModel as WithCoreFields).metadata, null);
    if (metadata !== null) {
        // TODO fix next line
        // eslint-disable-next-line
        for (const fieldId in metadata) {
            const fieldValue = metadata[fieldId] !== undefined ? metadata[fieldId] : '';

            if (customValidatorFields.includes(fieldId) && typeof (getCustomValidator(fieldId)(fieldValue, formValidationOptions)) === 'string') {
                return false;
            }
        }
    }

    return true;
};

export const isFormErrored = (currentFieldValidationErrors: FieldValidationErrors): boolean => {
    // TODO fix next line
    // eslint-disable-next-line
    for (const field in currentFieldValidationErrors) {
        if (currentFieldValidationErrors[field]) {
            logger.info(`Field '${field}' has errorId '${currentFieldValidationErrors[field]}'`);
            return true;
        }
    }
    return false;
};

const getValidationError = (
    fieldId: FieldId,
    value: FieldContent,
    defaultValidator: Validator,
    overriddenValidator?: Validator,
    formValidationOptions?: FormValidationOptions,
): ErrorId => {
    if (overriddenValidator) {
        logger.info(`Using overridden validator for field '${fieldId}'`);
        return overriddenValidator(value, formValidationOptions) as ErrorId;
    }
    return defaultValidator(value, formValidationOptions) as ErrorId;
};

const getEmptyFieldValidationErrors = (): FieldValidationErrors => {
    const emptyFieldValidationErrors: FieldValidationErrors = getFieldValidationErrorsEmpty();
    const emptyExtendedFieldValidationErrors: ExtendedFieldValidationErrors = getExtendedFieldValidationErrorsEmpty();
    const fieldValidationErrors: FieldValidationErrors = { ...emptyFieldValidationErrors, ...emptyExtendedFieldValidationErrors };
    return fieldValidationErrors;
};

const getCustomValidationError = (fieldId: ExtendedFieldId, value: FieldContent, formValidationOptions?: FormValidationOptions): ExtendedErrorId => {
    const customValidator: Validator = getCustomValidator(fieldId);
    logger.info(`Using custom validator for custom field '${fieldId}'`);
    return customValidator ? customValidator(value, formValidationOptions) : undefined;
};

const customValidatorExists = (fieldId: ExtendedFieldId): boolean => {
    const customValidatorFields = getCustomValidatorFields();

    if (customValidatorFields.length && customValidatorFields.indexOf(fieldId) >= 0) {
        return true;
    }
    return false;
};

const getFirstNameValidationError = (value: FieldContent): ErrorId | undefined => {
    const invalidError: ErrorId = 'invalidFirstName';

    if (value === undefined || !isValidName(value as string)) {
        return invalidError;
    }
};

const getLastNameValidationError = (value: string): ErrorId | undefined => {
    const invalidError: ErrorId = 'invalidLastName';

    if (value === undefined || !isValidName(value)) {
        return invalidError;
    }
};

const getMemberIdError = (): ErrorId | undefined => {
    // eslint-disable-next-line
    const invalidError: ErrorId = 'invalidMemberId';
    return undefined;
};

const getCountryValidationError = (value: string): ErrorId | undefined => {
    const invalidError: ErrorId = 'invalidCountry';
    if (!value) {
        return invalidError;
    }
};

const getEmailValidationError = (value: string): ErrorId | undefined => {
    const invalidError: ErrorId = 'invalidEmail';
    const VALID_EMAIL_REGEXP = /(^\s*?[A-Za-z0-9!#$%&''*+\/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&''*+\/=?^_`{|}~-]+)*@([A-Za-z0-9!#$%&''*+\/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&''*+\/=?^_`{|}~-]+)+|\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])\s*?$)/;

    if (value === undefined || !VALID_EMAIL_REGEXP.test(value)) {
        return invalidError;
    }
};


export const getPhoneNumberValidationError = (failIfValueFalsy: boolean) => (value: string): ErrorId => {
    const invalidPhoneNumber: ErrorId = 'invalidPhoneNumber';
    if (failIfValueFalsy && !value) {
        return invalidPhoneNumber;
    }

    const matchedPatterns = VALID_PHONE_NUMBER_REGEXPS.filter((regexp: RegExp) => regexp.test(value));

    if (value && matchedPatterns.length === 0) {
        return invalidPhoneNumber;
    }
};

const getPhoneNumberValidator = (formValidationOptions?: FormValidationOptions): Validator => getPhoneNumberValidationError(formValidationOptions && formValidationOptions.smsLoopEnabled);

const getOrganizationValidationError = (value: Organization): ErrorId | undefined => {
    const invalidError: ErrorId = 'invalidOrganization';
    if (value.id === undefined || Number.isNaN(value.id)
        || (value.source !== 'EMPLOYER' && value.id < 0)
        || (value.source === 'EMPLOYER' && (value.id > -1 || !value.idExtended))
        || (value.id === 0 && value.name.length <= 0)) {
        return invalidError;
    }
};

const isBirthDateEmpty = (birthDate: any) => {
    const noUndefined = birthDate.split('undefined').join('');
    return noUndefined.length <= 2;
};

const getBirthDateValidationError = (value: string, formValidationOptions?: FormValidationOptions): ErrorId | undefined => {
    const invalidError: ErrorId = 'invalidBirthDate';
    const underagePersonError: ErrorId = 'underagePerson';
    const futureBirthDateError: ErrorId = 'futureBirthDate';
    const outsideAgeError: ErrorId = 'outsideAgePerson';

    if (getSafe(() => formValidationOptions.currentStep === 'collectMemberPersonalInfo')) {
        if (value === undefined || value === '' || isBirthDateEmpty(value)) return;
        if (!isValidDateFormat(value) || !isValidDate(value)) {
            return invalidError;
        }
    } else if (value === undefined || !isValidDateFormat(value) || !isValidDate(value)) {
        return invalidError;
    }
    if (getSafe(() => formValidationOptions.minAge) && getSafe(() => formValidationOptions.maxAge)) {
        return isAgeOutsideRange(value, formValidationOptions.minAge, formValidationOptions.maxAge) ? outsideAgeError : null;
    }
    if (isFutureDate(value)) {
        return futureBirthDateError;
    }
    if (isUnderageDate(value)) {
        return underagePersonError;
    }
};

const getFileValidationError = (file: File): ErrorId | undefined => {
    const hasFileExtension = (fileName: string): boolean => fileName && fileName.includes('.');
    const noValidFilesError: ErrorId = 'noValidFiles';
    const invalidFileSizeMaxError: ErrorId = 'invalidFileSizeMax';
    const invalidFileSizeEmptyError: ErrorId = 'invalidFileSizeEmpty';
    const unsupportedDocMimeTypeError: ErrorId = 'unsupportedDocMimeType';
    const FILE_SIZE_10MB = 10485760;
    const validFileTypes: string[] = ACCEPTED_DOC_MIME_TYPES;

    if (file === undefined) {
        return noValidFilesError;
    }
    // If no extension, allow upload so API can determine if valid mime type
    if (hasFileExtension(file.name) && !validFileTypes.includes(file.type)) {
        return unsupportedDocMimeTypeError;
    }
    if (file.size > FILE_SIZE_10MB) {
        return invalidFileSizeMaxError;
    }
    if (file.size <= 0) {
        return invalidFileSizeEmptyError;
    }
};

export const getPostalCodeValidationError = (value: string, formValidationOptions?: FormValidationOptions): ErrorId | undefined => {
    const invalidPostalCode: ErrorId = 'invalidPostalCode';
    const countryCode = getSafe(() => formValidationOptions.viewModel.countryChoice.value, 'US');

    if (isValidPostalCode(countryCode, value)) {
        return;
    }
    return invalidPostalCode;
};

const getAddress1ValidationError = (value: string): ErrorId | undefined => {
    const invalidError: ErrorId = 'invalidAddress';
    if (!value) {
        return invalidError;
    }
};

const getCityValidationError = (value: string): ErrorId | undefined => {
    const invalidError: ErrorId = 'invalidCity';
    if (!value) {
        return invalidError;
    }
};

const getStateValidationError = (value: string): ErrorId | undefined => {
    const invalidError: ErrorId = 'invalidState';
    if (!isValidState(value)) {
        return invalidError;
    }
};

const getDischargeDateValidationError = (value: string, formValidationOptions?: FormValidationOptions): ErrorId | undefined => {
    const invalidDischargeDate: ErrorId = 'invalidDischargeDate';
    const futureDischargeDate: ErrorId = 'futureDischargeDate';
    const dischargeDateBeforeBirthDate: ErrorId = 'dischargeDateBeforeBirthDate';

    if (value === undefined || !isValidDateFormat(value) || !isValidDate(value)) {
        return invalidDischargeDate;
    }
    if (isFutureDate(value)) {
        return futureDischargeDate;
    }
    const dischargeDate = new Date(value);
    const birthDate = new Date((formValidationOptions.viewModel as InactiveMilitaryPersonalInfoViewModel).birthDate);
    if (dischargeDate < birthDate) {
        return dischargeDateBeforeBirthDate;
    }
};

const getSsnValidationError = (value: number): ErrorId | undefined => {
    const invalidSocialSecurityNumber: ErrorId = 'invalidSocialSecurityNumber';
    if (value.toString().length !== SSN_STRING_LENGTH) {
        return invalidSocialSecurityNumber;
    }
};

const getDriverLicenseNumberValidationError = (value: string): ErrorId | undefined => {
    const invalidDriverLicenseNumber: ErrorId = 'invalidDriverLicenseNumber';
    const VALID_DRIVER_LICENSE_REGEXP = /^[A-Za-z0-9-\*]{1,100}$/; // '*' is sometimes used in WA state

    if (value === undefined || !VALID_DRIVER_LICENSE_REGEXP.test(value)) {
        return invalidDriverLicenseNumber;
    }
};


const getStatusesValidationError = (value: string): ErrorId | undefined => {
    const invalidStatuses: ErrorId = 'invalidStatuses';
    if (!value || !isValidLicensedProfessionalStatus(value)) {
        return invalidStatuses;
    }
};

const isValidMilitaryStatus = (value: string) => {
    const validMilitaryStatuses: MilitaryStatus[] = ['ACTIVE_DUTY', 'VETERAN', 'RESERVIST', 'MILITARY_RETIREE', 'MILITARY_FAMILY'];
    if (validMilitaryStatuses.indexOf(value as MilitaryStatus) >= 0) {
        return true;
    }
    return false;
};


const isValidFirstResponderStatus = (value: string) => {
    const validFirstResponderStatuses: FirstResponderStatus[] = ['FIREFIGHTER', 'POLICE', 'EMT'];
    if (validFirstResponderStatuses.indexOf(value as FirstResponderStatus) >= 0) {
        return true;
    }
    return false;
};

const isValidMedicalProfessionalStatus = (value: string) => {
    const validMedicalProfessionalStatuses: MedicalProfessionalStatus[] = ['NURSE', 'DOCTOR', 'OTHER_HEALTH_WORKER', 'DENTIST', 'PHARMACIST'];
    if (validMedicalProfessionalStatuses.indexOf(value as MedicalProfessionalStatus) >= 0) {
        return true;
    }
    return false;
};

const getStatusValidationError = (value: string): ErrorId | undefined => {
    const invalidStatus: ErrorId = 'invalidStatus';

    if (!value
        || (!isValidMilitaryStatus(value)
            && !isValidFirstResponderStatus(value)
            && !isValidMedicalProfessionalStatus(value))) {
        return invalidStatus;
    }
};

const isValidLicensedProfessionalStatus = (value: string) => value.length > 0;

const isValidName = (value: string): boolean => {
    // eslint-disable-next-line
    const VALID_NAME_REGEXP = /((?:[A-Z_a-z\xAA\xB5\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0300-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u0483-\u052F\u0531-\u0556\u0559\u0560-\u0588\u0591-\u05BD\u05BF\u05C1\u05C2\u05C4\u05C5\u05C7\u05D0-\u05EA\u05EF-\u05F2\u0610-\u061A\u0620-\u065F\u066E-\u06D3\u06D5-\u06DC\u06E1-\u06E8\u06DF-\u06E4\u06ED-\u06EF\u06EA-\u06ED\u06FA-\u06FC\u06FF\u0710-\u074A\u074D-\u07B1\u07CA-\u07F5\u07FA\u07FD\u0800-\u082D\u0840-\u085B\u0860-\u086A\u08A0-\u08B4\u08B6-\u08BD\u08D3-\u08E1\u08E3-\u0963\u0971-\u0983\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BC-\u09C4\u09C7\u09C8\u09CB-\u09CE\u09D7\u09DC\u09DD\u09DF-\u09E3\u09F0\u09F1\u09FC\u09FE\u0A01-\u0A03\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A3C\u0A3E-\u0A42\u0A47\u0A48\u0A4B-\u0A4D\u0A51\u0A59-\u0A5C\u0A5E\u0A70-\u0A75\u0A81-\u0A83\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABC-\u0AC5\u0AC7-\u0AC9\u0ACB-\u0ACD\u0AD0\u0AE0-\u0AE3\u0AF9-\u0AFF\u0B01-\u0B03\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3C-\u0B44\u0B47\u0B48\u0B4B-\u0B4D\u0B56\u0B57\u0B5C\u0B5D\u0B5F-\u0B63\u0B71\u0B82\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BBE-\u0BC2\u0BC6-\u0BC8\u0BCA-\u0BCD\u0BD0\u0BD7\u0C00-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D-\u0C44\u0C46-\u0C48\u0C4A-\u0C4D\u0C55\u0C56\u0C58-\u0C5A\u0C60-\u0C63\u0C80-\u0C83\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBC-\u0CC4\u0CC6-\u0CC8\u0CCA-\u0CCD\u0CD5\u0CD6\u0CDE\u0CE0-\u0CE3\u0CF1\u0CF2\u0D00-\u0D03\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D44\u0D46-\u0D48\u0D4A-\u0D4E\u0D54-\u0D57\u0D5F-\u0D63\u0D7A-\u0D7F\u0D82\u0D83\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0DCA\u0DCF-\u0DD4\u0DD6\u0DD8-\u0DDF\u0DF2\u0DF3\u0E01-\u0E3A\u0E40-\u0E4E\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB9\u0EBB-\u0EBD\u0EC0-\u0EC4\u0EC6\u0EC8-\u0ECD\u0EDC-\u0EDF\u0F00\u0F18\u0F19\u0F35\u0F37\u0F39\u0F3E-\u0F47\u0F49-\u0F6C\u0F71-\u0F84\u0F86-\u0F97\u0F99-\u0FBC\u0FC6\u1000-\u103F\u1050-\u108F\u109A-\u109D\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u135D-\u135F\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16EE-\u16F8\u1700-\u170C\u170E-\u1714\u1720-\u1734\u1740-\u1753\u1760-\u176C\u176E-\u1770\u1772\u1773\u1780-\u17D3\u17D7\u17DC\u17DD\u180B-\u180D\u1820-\u1878\u1880-\u18AA\u18B0-\u18F5\u1900-\u191E\u1920-\u192B\u1930-\u193B\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A1B\u1A20-\u1A5E\u1A60-\u1A7C\u1A7F\u1AA7\u1AB0-\u1ABE\u1B00-\u1B4B\u1B6B-\u1B73\u1B80-\u1BAF\u1BBA-\u1BF3\u1C00-\u1C37\u1C4D-\u1C4F\u1C5A-\u1C7D\u1C80-\u1C88\u1C90-\u1CBA\u1CBD-\u1CBF\u1CD0-\u1CD2\u1CD4-\u1CF9\u1D00-\u1DF9\u1DFB-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u200C\u200D\u203F\u2040\u2054\u2071\u207F\u2090-\u209C\u20D0-\u20F0\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2160-\u2188\u24B6-\u24E9\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D7F-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2DE0-\u2DFF\u2E2F\u3005-\u3007\u3021-\u302F\u3031-\u3035\u3038-\u303C\u3041-\u3096\u3099\u309A\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312F\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FEF\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA672\uA674-\uA67D\uA67F-\uA6F1\uA717-\uA71F\uA722-\uA788\uA78B-\uA7B9\uA7F7-\uA827\uA840-\uA873\uA880-\uA8C5\uA8E0-\uA8F7\uA8FB\uA8FD-\uA8FF\uA90A-\uA92D\uA930-\uA953\uA960-\uA97C\uA980-\uA9C0\uA9CF\uA9E0-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA36\uAA40-\uAA4D\uAA60-\uAA76\uAA7A-\uAAC2\uAADB-\uAADD\uAAE0-\uAAEF\uAAF2-\uAAF6\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB65\uAB70-\uABEA\uABEC\uABED\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE00-\uFE0F\uFE20-\uFE2F\uFE33\uFE34\uFE4D-\uFE4F\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF3F\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]|\uD800[\uDC00-\uDC0B\uDC0D-\uDC26\uDC28-\uDC3A\uDC3C\uDC3D\uDC3F-\uDC4D\uDC50-\uDC5D\uDC80-\uDCFA\uDD40-\uDD74\uDDFD\uDE80-\uDE9C\uDEA0-\uDED0\uDEE0\uDF00-\uDF1F\uDF2D-\uDF4A\uDF50-\uDF7A\uDF80-\uDF9D\uDFA0-\uDFC3\uDFC8-\uDFCF\uDFD1-\uDFD5]|\uD801[\uDC00-\uDC9D\uDCB0-\uDCD3\uDCD8-\uDCFB\uDD00-\uDD27\uDD30-\uDD63\uDE00-\uDF36\uDF40-\uDF55\uDF60-\uDF67]|\uD802[\uDC00-\uDC05\uDC08\uDC0A-\uDC35\uDC37\uDC38\uDC3C\uDC3F-\uDC55\uDC60-\uDC76\uDC80-\uDC9E\uDCE0-\uDCF2\uDCF4\uDCF5\uDD00-\uDD15\uDD20-\uDD39\uDD80-\uDDB7\uDDBE\uDDBF\uDE00-\uDE03\uDE05\uDE06\uDE0C-\uDE13\uDE15-\uDE17\uDE19-\uDE35\uDE38-\uDE3A\uDE3F\uDE60-\uDE7C\uDE80-\uDE9C\uDEC0-\uDEC7\uDEC9-\uDEE6\uDF00-\uDF35\uDF40-\uDF55\uDF60-\uDF72\uDF80-\uDF91]|\uD803[\uDC00-\uDC48\uDC80-\uDCB2\uDCC0-\uDCF2\uDD00-\uDD27\uDF00-\uDF1C\uDF27\uDF30-\uDF50]|\uD804[\uDC00-\uDC46\uDC82-\uDCBA\uDC7F-\uDC82\uDCD0-\uDCE8\uDD00-\uDD34\uDD44-\uDD46\uDD50-\uDD73\uDD76\uDD80-\uDDC4\uDDC9-\uDDCC\uDDDA\uDDDC\uDE00-\uDE11\uDE13-\uDE37\uDE3E\uDE80-\uDE86\uDE88\uDE8A-\uDE8D\uDE8F-\uDE9D\uDE9F-\uDEA8\uDEB0-\uDEEA\uDF00-\uDF03\uDF05-\uDF0C\uDF0F\uDF10\uDF13-\uDF28\uDF2A-\uDF30\uDF32\uDF33\uDF35-\uDF39\uDF3B-\uDF44\uDF47\uDF48\uDF4B-\uDF4D\uDF50\uDF57\uDF5D-\uDF63\uDF66-\uDF6C\uDF70-\uDF74]|\uD805[\uDC00-\uDC4A\uDC5E\uDC80-\uDCC5\uDCC7\uDD80-\uDDB5\uDDB8-\uDDC0\uDDD8-\uDDDD\uDE00-\uDE40\uDE44\uDE80-\uDEB7\uDF00-\uDF1A\uDF1D-\uDF2B]|\uD806[\uDC00-\uDC3A\uDCA0-\uDCDF\uDCFF\uDE00-\uDE3E\uDE47\uDE50-\uDE83\uDE86-\uDE99\uDE9D\uDEC0-\uDEF8]|\uD807[\uDC00-\uDC08\uDC0A-\uDC36\uDC38-\uDC40\uDC72-\uDC8F\uDC92-\uDCA7\uDCA9-\uDCB6\uDD00-\uDD06\uDD08\uDD09\uDD0B-\uDD36\uDD3A\uDD3C\uDD3D\uDD3F-\uDD47\uDD60-\uDD65\uDD67\uDD68\uDD6A-\uDD8E\uDD90\uDD91\uDD93-\uDD98\uDEE0-\uDEF6]|\uD808[\uDC00-\uDF99]|\uD809[\uDC00-\uDC6E\uDC80-\uDD43]|[\uD80C\uD81C-\uD820\uD840-\uD868\uD86A-\uD86C\uD86F-\uD872\uD874-\uD879][\uDC00-\uDFFF]|\uD80D[\uDC00-\uDC2E]|\uD811[\uDC00-\uDE46]|\uD81A[\uDC00-\uDE38\uDE40-\uDE5E\uDED0-\uDEED\uDEF0-\uDEF4\uDF00-\uDF36\uDF40-\uDF43\uDF63-\uDF77\uDF7D-\uDF8F]|\uD81B[\uDE40-\uDE7F\uDF00-\uDF44\uDF50-\uDF7E\uDF8F-\uDF9F\uDFE0\uDFE1]|\uD821[\uDC00-\uDFF1]|\uD822[\uDC00-\uDEF2]|\uD82C[\uDC00-\uDD1E\uDD70-\uDEFB]|\uD82F[\uDC00-\uDC6A\uDC70-\uDC7C\uDC80-\uDC88\uDC90-\uDC99\uDC9D\uDC9E]|\uD834[\uDD65-\uDD69\uDD6D-\uDD72\uDD7B-\uDD82\uDD85-\uDD8B\uDDAA-\uDDAD\uDE42-\uDE44]|\uD835[\uDC00-\uDC54\uDC56-\uDC9C\uDC9E\uDC9F\uDCA2\uDCA5\uDCA6\uDCA9-\uDCAC\uDCAE-\uDCB9\uDCBB\uDCBD-\uDCC3\uDCC5-\uDD05\uDD07-\uDD0A\uDD0D-\uDD14\uDD16-\uDD1C\uDD1E-\uDD39\uDD3B-\uDD3E\uDD40-\uDD44\uDD46\uDD4A-\uDD50\uDD52-\uDEA5\uDEA8-\uDEC0\uDEC2-\uDEDA\uDEDC-\uDEFA\uDEFC-\uDF14\uDF16-\uDF34\uDF36-\uDF4E\uDF50-\uDF6E\uDF70-\uDF88\uDF8A-\uDFA8\uDFAA-\uDFC2\uDFC4-\uDFCB]|\uD836[\uDE00-\uDE36\uDE3B-\uDE6C\uDE75\uDE84\uDE9B-\uDE9F\uDEA1-\uDEAF]|\uD838[\uDC00-\uDC06\uDC08-\uDC18\uDC1B-\uDC21\uDC23\uDC24\uDC26-\uDC2A]|\uD83A[\uDC00-\uDCC4\uDCD0-\uDCD6\uDD00-\uDD4A]|\uD83B[\uDE00-\uDE03\uDE05-\uDE1F\uDE21\uDE22\uDE24\uDE27\uDE29-\uDE32\uDE34-\uDE37\uDE39\uDE3B\uDE42\uDE47\uDE49\uDE4B\uDE4D-\uDE4F\uDE51\uDE52\uDE54\uDE57\uDE59\uDE5B\uDE5D\uDE5F\uDE61\uDE62\uDE64\uDE67-\uDE6A\uDE6C-\uDE72\uDE74-\uDE77\uDE79-\uDE7C\uDE7E\uDE80-\uDE89\uDE8B-\uDE9B\uDEA1-\uDEA3\uDEA5-\uDEA9\uDEAB-\uDEBB]|\uD83C[\uDD30-\uDD49\uDD50-\uDD69\uDD70-\uDD89]|\uD869[\uDC00-\uDED6\uDF00-\uDFFF]|\uD86D[\uDC00-\uDF34\uDF40-\uDFFF]|\uD86E[\uDC00-\uDC1D\uDC20-\uDFFF]|\uD873[\uDC00-\uDEA1\uDEB0-\uDFFF]|\uD87A[\uDC00-\uDFE0]|\uD87E[\uDC00-\uDE1D]|\uDB40[\uDD00-\uDDEF])+)/g;
    return VALID_NAME_REGEXP.test(value);
};

const isValidDate = (value: string): boolean => {
    const dateParts = value.split('-');
    const dateYear = parseInt(dateParts[0], DECIMAL_RADIX);
    const dateMonth = parseInt(dateParts[1], DECIMAL_RADIX);
    const dateDay = parseInt(dateParts[2], DECIMAL_RADIX);
    const monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (dateYear < MIN_BIRTHYEAR || dateYear > MAX_BIRTHYEAR || dateMonth <= 0 || dateMonth > 12) {
        return false;
    }

    // Check for leap years
    if (dateYear % 400 === 0 || (dateYear % 100 !== 0 && dateYear % 4 === 0)) {
        monthDays[1] = 29;
    }

    if (dateDay <= 0 || dateDay > monthDays[dateMonth - 1]) {
        return false;
    }
    return true;
};

const isValidDateFormat = (value: string): boolean => {
    const VALID_DATE_FORMAT_REGEXP = /^\d{4}[\-\s\/]\d{1,2}[\-\s\/]\d{1,2}$/;

    if (VALID_DATE_FORMAT_REGEXP.test(value)) {
        return true;
    }
    return false;
};

const isFutureDate = (value: string): boolean => {
    const currentDate = new Date();
    const candidateDate = new Date(value);
    return candidateDate > currentDate;
};

const isUnderageDate = (value: string, minAge = ADOLESCENT_AGE): boolean => {
    const birthDateParts = value.split('-');
    const birthDateYear = parseInt(birthDateParts[0], DECIMAL_RADIX);
    const birthDateMonth = parseInt(birthDateParts[1], DECIMAL_RADIX);
    const birthDateDay = parseInt(birthDateParts[2], DECIMAL_RADIX);
    const currentYear = (new Date()).getFullYear();
    const currentMonth = (new Date()).getMonth() + 1;
    const currentDay = (new Date()).getDate();

    if ((currentYear - birthDateYear) === minAge) {
        if ((currentMonth - birthDateMonth) < 0) {
            return true;
        }

        if ((currentMonth - birthDateMonth) === 0) {
            if ((currentDay - birthDateDay) < 0) {
                return true;
            }
        }
    } else if ((currentYear - birthDateYear) < minAge) {
        return true;
    }
    return false;
};

const isOverageDate = (value: string, maxAge = 120) => {
    const birthDateParts = value.split('-');
    const birthDateYear = parseInt(birthDateParts[0], DECIMAL_RADIX);
    const birthDateMonth = parseInt(birthDateParts[1], DECIMAL_RADIX);
    const birthDateDay = parseInt(birthDateParts[2], DECIMAL_RADIX);
    const currentYear = (new Date()).getFullYear();
    const currentMonth = (new Date()).getMonth() + 1;
    const currentDay = (new Date()).getDate();

    if ((currentYear - birthDateYear) === maxAge) {
        if ((currentMonth - birthDateMonth) > 0) {
            return true;
        }

        if ((currentMonth - birthDateMonth) === 0) {
            if ((currentDay - birthDateDay) > 0) {
                return true;
            }
        }
    } else if ((currentYear - birthDateYear) > maxAge) {
        return true;
    }
    return false;
};

const isAgeOutsideRange = (value: string, minAge: number, maxAge: number): boolean => isUnderageDate(value, minAge) || isOverageDate(value, maxAge);

export const isValidPostalCode = (countryCode: Country = 'US', value: string) => {
    const countryCodeString = countryCode as any;
    if (postcodeValidatorExistsForCountry(countryCodeString)) {
        return postcodeValidator(value, countryCodeString);
    }
    return false;
};

/**
 * @deprecated Use `isValidPostalCode('US', value)` instead
 *  */
export const isValidUsPostalCode = (value: string): boolean => isValidPostalCode('US', value);

/**
 * @deprecated Use `isValidPostalCode('CA', value)` instead
 *  */
export const isValidCanadianPostalCode = (value: string): boolean => isValidPostalCode('CA', value);

const isValidState = (value: string) => {
    if (!value) {
        return false;
    }
    const validStateCodes: StateEnum[] = Object.values(StateEnum);
    if (validStateCodes.indexOf(value.toUpperCase() as StateEnum) >= 0) {
        return true;
    }
    return false;
};


export const validateFieldById = (
    fieldId: FieldId | ExtendedFieldId,
    value: FieldContent,
    formValidationOptions?: FormValidationOptions,
): ErrorId | ExtendedErrorId | undefined => {
    assertValidFieldId(fieldId);
    let defaultValidator: Validator;
    const overriddenValidator: Validator = getOverridenValidator(fieldId as FieldId);

    switch (fieldId) {
        case FieldIdEnum.firstName:
            defaultValidator = getFirstNameValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.lastName:
            defaultValidator = getLastNameValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.memberId:
            defaultValidator = getMemberIdError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.email:
            defaultValidator = getEmailValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.phoneNumber:
            defaultValidator = getPhoneNumberValidator(formValidationOptions);
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.organization:
            defaultValidator = getOrganizationValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.birthDate:
            defaultValidator = getBirthDateValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.postalCode:
            defaultValidator = getPostalCodeValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.address1:
            defaultValidator = getAddress1ValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.city:
            defaultValidator = getCityValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.state:
            defaultValidator = getStateValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.country:
            defaultValidator = getCountryValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.dischargeDate:
            defaultValidator = getDischargeDateValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.socialSecurityNumber:
            defaultValidator = getSsnValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.status:
            defaultValidator = getStatusValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.statuses:
            defaultValidator = getStatusesValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.driverLicenseNumber:
            defaultValidator = getDriverLicenseNumberValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        case FieldIdEnum.docUpload:
            defaultValidator = getFileValidationError;
            return getValidationError(fieldId, value, defaultValidator, overriddenValidator, formValidationOptions);

        default:
            if (customValidatorExists(fieldId)) {
                return getCustomValidationError(fieldId, value, formValidationOptions);
            }
    }
};
